import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DateTime, InlineAlert, useToast } from '@/components/ui';
import { useAuthStore } from '@/stores/authStore';
import { activeUnlocks, endUnlock, sensitiveAccessChanged, SENSITIVE_ACCESS_CHANGED } from './sensitiveAccessApi';
import type { SensitiveGrant } from './sensitiveAccessApi';

/** Auth-scoped polling plus an exact local expiry timer; never keep an expired banner. */
export function SensitiveAccessBanner() {
  const user = useAuthStore((state) => state.user);
  const { pathname, search } = useLocation();
  const { push } = useToast();
  const identity = `${user?.tenantId ?? ''}:${user?.id ?? ''}`;
  const [owner, setOwner] = useState('');
  const [grants, setGrants] = useState<SensitiveGrant[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setGrants([]);
    setError('');
    setOwner(identity);
    if (!user?.id || !user.tenantId) return;
    let disposed = false;
    let request: AbortController | undefined;
    let version = 0;
    async function refresh() {
      request?.abort();
      request = new AbortController();
      const current = ++version;
      try {
        const result = await activeUnlocks(request.signal);
        if (!disposed && current === version) {
          setGrants(result.data.filter((grant) => !grant.revoked_at && Date.parse(grant.expires_at) > Date.now()));
          setError('');
        }
      } catch {
        if (!disposed && current === version && !request.signal.aborted) {
          setError('Unable to refresh sensitive access status.');
        }
      }
    }
    void refresh();
    const poll = window.setInterval(() => { void refresh(); }, 30_000);
    const changed = () => { void refresh(); };
    window.addEventListener(SENSITIVE_ACCESS_CHANGED, changed);
    window.addEventListener('focus', changed);
    return () => {
      disposed = true;
      request?.abort();
      window.clearInterval(poll);
      window.removeEventListener(SENSITIVE_ACCESS_CHANGED, changed);
      window.removeEventListener('focus', changed);
    };
  }, [user?.id, user?.tenantId, identity, pathname, search]);

  useEffect(() => {
    if (!grants.length) return;
    const next = Math.min(...grants.map((grant) => Date.parse(grant.expires_at)));
    const timer = window.setTimeout(() => {
      setGrants((current) => current.filter((grant) => Date.parse(grant.expires_at) > Date.now()));
      sensitiveAccessChanged();
    }, Math.max(0, next - Date.now()));
    return () => window.clearTimeout(timer);
  }, [grants]);

  if (owner !== identity || (!grants.length && !error)) return null;
  return <section aria-label="Sensitive access">
    {grants.map((grant) => <InlineAlert key={grant.id} tone="warning"
      action={{ id: `end-${grant.id}`, label: 'End now', run: async () => {
        await endUnlock(grant.id);
        setGrants((current) => current.filter((item) => item.id !== grant.id));
        push({ tone: 'success', title: 'Sensitive view ended' });
        sensitiveAccessChanged();
      } }}>
      Sensitive view unlocked until <DateTime value={grant.expires_at} format="time" />
      {' — reason: '}{grant.justification}
      {' — '}{grant.scope === 'tenant' ? 'Entire tenant (own Restricted fields excluded)' : grant.subject || 'Selected person'}
    </InlineAlert>)}
    {error && <InlineAlert tone="danger">{error}</InlineAlert>}
  </section>;
}
